import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

import { Badge } from '../ui/badge';

import { Users, Clock, CheckCircle, FileText, TrendingUp, DollarSign, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ShieldCheck, Award, Eye, Mail, PenLine } from 'lucide-react';

export function StatsCards({ stats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="domium-stats">
      <Card className="border-0 shadow-lg bg-gradient-to-br from-violet-600 to-violet-700 text-white">
        <CardContent className="p-4 text-center">
          <FileText className="h-6 w-6 mx-auto mb-1 opacity-80" />
          <p className="text-2xl font-bold">{stats.total}</p>
          <p className="text-xs opacity-80">Total</p>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
        <CardContent className="p-4 text-center">
          <CheckCircle className="h-6 w-6 mx-auto mb-1 opacity-80" />
          <p className="text-2xl font-bold">{stats.actives}</p>
          <p className="text-xs opacity-80">Actives</p>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-amber-600 text-white">
        <CardContent className="p-4 text-center">
          <Clock className="h-6 w-6 mx-auto mb-1 opacity-80" />
          <p className="text-2xl font-bold">{stats.en_attente_paiement}</p>
          <p className="text-xs opacity-80">Att. paiement</p>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <CardContent className="p-4 text-center">
          <Users className="h-6 w-6 mx-auto mb-1 opacity-80" />
          <p className="text-2xl font-bold">{stats.en_cours}</p>
          <p className="text-xs opacity-80">En cours</p>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-500 to-orange-600 text-white">
        <CardContent className="p-4 text-center">
          <ShieldCheck className="h-6 w-6 mx-auto mb-1 opacity-80" />
          <p className="text-2xl font-bold">{stats.avec_attestation_provisoire}</p>
          <p className="text-xs opacity-80">Att. provisoire</p>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <CardContent className="p-4 text-center">
          <Award className="h-6 w-6 mx-auto mb-1 opacity-80" />
          <p className="text-2xl font-bold">{stats.avec_attestation_definitive}</p>
          <p className="text-xs opacity-80">Att. definitive</p>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-lg bg-gradient-to-br from-indigo-600 to-indigo-700 text-white">
        <CardContent className="p-4 text-center">
          <DollarSign className="h-6 w-6 mx-auto mb-1 opacity-80" />
          <p className="text-2xl font-bold">{(stats.montant_total || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</p>
          <p className="text-xs opacity-80">Montant Global (EUR)</p>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-lg bg-gradient-to-br from-green-600 to-green-700 text-white">
        <CardContent className="p-4 text-center">
          <TrendingUp className="h-6 w-6 mx-auto mb-1 opacity-80" />
          <p className="text-2xl font-bold">{(stats.montant_encaisse || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}</p>
          <p className="text-xs opacity-80">Encaisse (EUR)</p>
        </CardContent>
      </Card>
    </div>
  );
}

export function PackTrackingBadge({ tracking, idx }) {
  if (!tracking || tracking.sent_count === 0) return null;
  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
    } catch { return iso; }
  };
  const consulted = tracking.access_count > 0;
  const expired = tracking.expires_at && new Date(tracking.expires_at) < new Date();
  // V37.94 — suivi PAR destinataire (envois multiples)
  const recips = tracking.recipients || [];
  const openedCount = recips.filter(r => r.access_count > 0).length;
  const multi = recips.length > 1;
  const { color, icon, label } = consulted
    ? { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle, label: multi ? `Ouvert ${openedCount}/${recips.length}` : `Consulté (${tracking.access_count})` }
    : expired
    ? { color: 'bg-gray-100 text-gray-600 border-gray-300', icon: Mail, label: 'Lien expiré' }
    : { color: 'bg-violet-100 text-violet-800 border-violet-300', icon: Mail, label: multi ? `Envoyé (${recips.length} dest.)` : 'Envoyé' };
  const Icon = icon;
  const recipLines = multi
    ? [
        `— Destinataires (${recips.length}) —`,
        ...recips.map(r => `${r.email}${r.name ? ' (' + r.name + ')' : ''} : envoyé ${fmt(r.last_sent_at)} · ${r.access_count > 0 ? `ouvert (${r.access_count}, dernier ${fmt(r.last_accessed_at)})` : 'non ouvert'}`),
      ]
    : [];
  const tooltipLines = [
    `Envoi : ${fmt(tracking.last_sent_at)}`,
    tracking.to_email ? `À : ${tracking.to_name ? tracking.to_name + ' <' + tracking.to_email + '>' : tracking.to_email}` : null,
    tracking.last_sent_by ? `Par : ${tracking.last_sent_by}` : null,
    tracking.expires_at ? `Expire : ${fmt(tracking.expires_at)}${expired ? ' (expiré)' : ''}` : null,
    consulted ? `Dernière consultation : ${fmt(tracking.last_accessed_at)}` : 'Non consulté',
    consulted && tracking.access_count > 1 ? `Total consultations : ${tracking.access_count}` : null,
    tracking.sent_count > 1 ? `Nb envois : ${tracking.sent_count}` : null,
    ...recipLines,
  ].filter(Boolean).join('\n');
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs rounded border ${color} cursor-help`}
      title={tooltipLines}
      data-testid={`pack-tracking-badge-${idx}`}
    >
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

export function NominativeTrackingBadge({ tracking, idx }) {
  if (!tracking || (!tracking.sent_count && !tracking.signed)) return null;
  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
    } catch { return iso; }
  };
  const consulted = tracking.access_count > 0;
  const expired = !tracking.signed && tracking.expires_at && new Date(tracking.expires_at) < new Date();
  const { color, Icon, label } = tracking.signed
    ? { color: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: ShieldCheck, label: 'Signée MO' }
    : consulted
    ? { color: 'bg-sky-100 text-sky-800 border-sky-300', Icon: Eye, label: `Sign. consultée (${tracking.access_count})` }
    : expired
    ? { color: 'bg-gray-100 text-gray-600 border-gray-300', Icon: PenLine, label: 'Lien sign. expiré' }
    : { color: 'bg-violet-100 text-violet-800 border-violet-300', Icon: PenLine, label: 'Sign. envoyée' };
  const tooltip = [
    "Signature électronique de l'attestation nominative (MO)",
    tracking.last_sent_at ? `Lien envoyé : ${fmt(tracking.last_sent_at)}` : null,
    tracking.to_email ? `À : ${tracking.to_email}` : null,
    !tracking.signed && tracking.expires_at ? `Expire : ${fmt(tracking.expires_at)}${expired ? ' (expiré)' : ''}` : null,
    consulted
      ? `Page de signature consultée : ${fmt(tracking.last_accessed_at)} (${tracking.access_count} consultation${tracking.access_count > 1 ? 's' : ''})`
      : 'Page de signature non consultée',
    tracking.signed ? `Signée par ${tracking.signer_name || 'le MO'} le ${fmt(tracking.signed_at)}` : null,
  ].filter(Boolean).join('\n');
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs rounded border ${color} cursor-help`}
      title={tooltip}
      data-testid={`nominative-tracking-badge-${idx}`}
    >
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

export function SignatureBadge({ statut }) {
  if (!statut || statut === 'non_initie') return <span className="text-xs text-gray-400">-</span>;
  if (statut === 'signed') return <Badge className="bg-emerald-600 text-white text-xs font-semibold">Signee</Badge>;
  if (statut === 'otp_pending' || statut === 'pending') return <Badge className="bg-amber-600 text-white text-xs font-semibold">En attente</Badge>;
  return <Badge className="bg-gray-500 text-white text-xs font-semibold">{statut}</Badge>;
}

export function Pagination({ page, totalPages, setPage, total = null, pageSize = null, setPageSize = null }) {
  // V37.89 — Pagination enrichie : plage de résultats, taille de page,
  // boutons première/dernière page. Rétro-compatible (props optionnelles).
  if (totalPages <= 1 && !setPageSize) return null;
  const from = total !== null && pageSize ? Math.min((page - 1) * pageSize + 1, total) : null;
  const to = total !== null && pageSize ? Math.min(page * pageSize, total) : null;
  return (
    <div className="flex items-center justify-between flex-wrap gap-3 mt-4 pt-4 border-t" data-testid="domium-pagination">
      <div className="flex items-center gap-3 text-sm text-gray-500">
        {total !== null && pageSize && (
          <span data-testid="pagination-range">
            {total === 0 ? '0 résultat' : `${from}–${to} sur ${total} résultat(s)`}
          </span>
        )}
        {setPageSize && (
          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white text-gray-700 hover:border-violet-400 cursor-pointer"
            data-testid="pagination-page-size"
            title="Nombre de lignes par page"
          >
            {[10, 20, 50, 100].map(n => (
              <option key={n} value={n}>{n} / page</option>
            ))}
          </select>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <Button variant="outline" size="sm" className="px-2" disabled={page <= 1} onClick={() => setPage(1)}
                title="Première page" data-testid="pagination-first">
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                data-testid="pagination-prev">
          <ChevronLeft className="h-4 w-4 mr-1" /> Precedent
        </Button>
        <span className="text-sm text-gray-600 font-medium px-2 whitespace-nowrap" data-testid="pagination-info">
          Page {page} / {totalPages}
        </span>
        <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                data-testid="pagination-next">
          Suivant <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
        <Button variant="outline" size="sm" className="px-2" disabled={page >= totalPages} onClick={() => setPage(totalPages)}
                title="Dernière page" data-testid="pagination-last">
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
